import type { ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface PendenciaItem {
  key: string;
  label: string;
  count: number;
  to?: string;
  icon?: ReactNode;
  severity?: 'default' | 'warning' | 'error' | 'success';
}

export interface PendenciasListProps {
  title: string;
  items: PendenciaItem[];
  loading?: boolean;
}

export function PendenciasList({ title, items, loading }: PendenciasListProps): JSX.Element {
  const { t } = useTranslation();
  const nonEmpty = items.filter((i) => i.count > 0);

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="overline" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
      {loading ? (
        <Box sx={{ px: 2, pb: 2 }}>
          <Skeleton height={48} />
          <Skeleton height={48} />
        </Box>
      ) : nonEmpty.length === 0 ? (
        <Box sx={{ px: 2, pb: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('features.dashboard.pendencias.allClear')}
          </Typography>
        </Box>
      ) : (
        <List dense disablePadding>
          {nonEmpty.map((item) => {
            const inner = (
              <>
                {item.icon && <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>}
                <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
                <Chip
                  size="small"
                  label={item.count}
                  color={item.severity ?? 'warning'}
                  variant={item.count > 0 ? 'filled' : 'outlined'}
                  sx={{ mr: item.to ? 0.5 : 0 }}
                />
                {item.to && <ChevronRightIcon fontSize="small" color="action" />}
              </>
            );
            if (item.to) {
              return (
                <ListItemButton key={item.key} component={RouterLink} to={item.to}>
                  {inner}
                </ListItemButton>
              );
            }
            return (
              <Box
                key={item.key}
                sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, gap: 1 }}
              >
                {inner}
              </Box>
            );
          })}
        </List>
      )}
    </Card>
  );
}
